import React from 'react';
import { ringBorder } from '../../portalkit/tokens';

/*
 * The Portal field kit — one set of primitives for every surface that edits a Portal Page.
 *
 * Born as the Portal Editor's own (design_handoff_portal_profile_editor "Screen 2", Oct 2 2026)
 * and shared with Onboarding v2 (design_handoff_onboarding_v2, Oct 12 2026), which asks for the
 * same fields on both screens so a change lands in both. Cards are #111 with the #1F1F1F inset
 * ring, radius 28 (22 on a phone). Inputs are pills on #000 with a #2A2A2A ring and a 2px white
 * ring on focus — 48px in the editor, 50 in onboarding (`inputStyle({ height })`). Toggles are
 * 46×26 tracks. Chips are 36px pills, #161616 with a #2A2A2A ring when unpicked.
 */

export const SERIF = "'Times New Roman', Times, serif";
export const RING_ON = ringBorder('#000');
export const RING_ON_CARD = ringBorder('#1A1A1A');

export const T = {
  title: { fontSize: 12, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase' },
  help: { fontSize: 12, lineHeight: 1.55, color: '#8A8A8A' },
  label: { fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase' },
  count: { fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: '#6B6B6B' },
};

export function Card({ phone, children, style, ...rest }) {
  return (
    <section style={{ background: '#111', boxShadow: 'inset 0 0 0 1px #1F1F1F', borderRadius: phone ? 22 : 28, padding: phone ? '18px 16px' : '24px 26px', display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0, ...style }} {...rest}>
      {children}
    </section>
  );
}

export function CardHead({ title, help, right, tag }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: '1 1 240px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span style={T.title}>{title}</span>{tag}</span>
        {help && <span style={T.help}>{help}</span>}
      </span>
      {right}
    </div>
  );
}

export const inputStyle = (extra) => ({ height: 48, padding: '0 20px', border: 'none', borderRadius: 999, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A', fontFamily: 'inherit', fontSize: 15, color: '#fff', outline: 'none', minWidth: 0, width: '100%', boxSizing: 'border-box', ...extra });

export function Field({ label, children, hint, error }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <span style={T.label}>{label}</span>
      {children}
      {error ? <span role="alert" style={{ fontSize: 11, lineHeight: 1.5, color: '#FF6B6B' }}>{error}</span> : hint ? <span style={{ fontSize: 11, lineHeight: 1.5, color: '#6B6B6B' }}>{hint}</span> : null}
    </label>
  );
}

export function TextInput({ value, onChange, style, ...rest }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} className="pe-in" style={inputStyle(style)} {...rest} />;
}

/**
 * A pill input with a fixed prefix (`heyportal.app/`, `@`) and the typed part in `color`.
 *
 * `style` is destructured out of `rest` on purpose, and lands on the PILL — it is the field's own
 * box, so a caller asking for `height` means the pill, not the bare input inside it. Leaving it in
 * `rest` put it on the `<input>` AFTER that input's own `style`, so a caller passing
 * `style={undefined}` (which `HandleField` does whenever no height is given — every caller today)
 * overrode the whole lot with nothing: React dropped the inline styles and the browser drew its
 * default white square box inside the dark pill. Any prop spread after a `style` wins, `undefined`
 * included.
 */
export function PrefixInput({ prefix, value, onChange, color = '#fff', fontSize = 15, invalid, style, ...rest }) {
  return (
    <span className="pe-in-wrap" style={{ display: 'flex', alignItems: 'center', height: 48, padding: '0 20px', borderRadius: 999, background: '#000', boxShadow: `inset 0 0 0 1px ${invalid ? '#FF6B6B' : '#2A2A2A'}`, boxSizing: 'border-box', ...style }}>
      <span style={{ fontSize: fontSize - 1, color: '#6B6B6B', whiteSpace: 'nowrap' }}>{prefix}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={{ flex: 1, minWidth: 0, height: '100%', border: 'none', background: 'none', boxShadow: 'none', outline: 'none', fontFamily: 'inherit', fontSize: fontSize - 1, color }} {...rest} />
    </span>
  );
}

/** 46×26 toggle. On: white track, black knob at 23px. Off: #2A2A2A track, #8A8A8A knob at 3px. */
export function Toggle({ on, onToggle, label }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} onClick={onToggle} style={{ flexShrink: 0, position: 'relative', width: 46, height: 26, border: 'none', borderRadius: 999, background: on ? '#fff' : '#2A2A2A', cursor: 'pointer', transition: 'background-color .2s', padding: 0 }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 23 : 3, width: 20, height: 20, borderRadius: '50%', background: on ? '#000' : '#8A8A8A', transition: 'left .2s, background-color .2s' }} />
    </button>
  );
}

export function ToggleRow({ on, onToggle, label, note, children, style }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 12, ...style }}>
      <Toggle on={on} onToggle={onToggle} label={label} />
      <span style={{ flex: 1, minWidth: 0, fontSize: 12, lineHeight: 1.5, color: '#BDBDBD' }}>{children || label}{note && <span style={{ color: '#6B6B6B' }}> {note}</span>}</span>
    </span>
  );
}

/** A small round button (reorder arrows, remove). */
export function RoundButton({ size = 28, dim, color = '#fff', children, style, ...rest }) {
  return (
    <button type="button" style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: '50%', background: '#1F1F1F', color, fontSize: 9, cursor: dim ? 'default' : 'pointer', opacity: dim ? 0.3 : 1, padding: 0, flexShrink: 0, ...style }} {...rest}>{children}</button>
  );
}

export function SmallPill({ children, bg = '#1F1F1F', fg = '#fff', height = 36, style, ...rest }) {
  return <button type="button" className="pk-btn" style={{ height, padding: '0 16px', background: bg, color: fg, fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', ...style }} {...rest}>{children}</button>;
}

/** DESKTOP | MOBILE segmented control on a #000 track; the active pill is white. */
export function DeviceSwitch({ value, onChange, height = 36, fontSize = 10 }) {
  return (
    <span role="tablist" aria-label="Preview device" style={{ display: 'flex', gap: 2, padding: 3, borderRadius: 999, background: '#000', flexShrink: 0 }}>
      {[['desktop', 'DESKTOP'], ['mobile', 'MOBILE']].map(([k, l]) => (
        <button key={k} type="button" role="tab" aria-selected={value === k} onClick={() => onChange(k)} className="pk-btn" style={{ height, padding: `0 ${height >= 36 ? 16 : 12}px`, background: value === k ? '#fff' : 'transparent', color: value === k ? '#000' : '#fff', fontSize, fontWeight: 700, letterSpacing: '.14em' }}>{l}</button>
      ))}
    </span>
  );
}

/** A selectable chip: min-height 36, selected in the given colours, unselected on #000 with a #333 ring. */
export function Chip({ selected, disabled, onClick, bg = '#fff', fg = '#000', children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={!!selected} style={{ minHeight: 36, padding: '6px 14px', border: 'none', borderRadius: 999, background: selected ? bg : '#161616', color: selected ? fg : '#fff', boxShadow: selected ? 'none' : 'inset 0 0 0 1px #2A2A2A', opacity: disabled ? 0.35 : 1, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background-color .2s, color .2s' }}>{children}</button>
  );
}

/**
 * The iridescent ring as a circle: `border: Npx solid transparent` + the ring as a border-box
 * layer, sliding over 3s (still under prefers-reduced-motion, via `.pk-ring-anim`).
 * Onboarding's cover slot, its success screen and the profile photo gate all wear it.
 */
export function IrisCircle({ size, fill = '#111', border = 3, children, className = '', style, ...rest }) {
  const Tag = rest.onClick ? 'button' : 'span';
  return (
    <Tag
      {...(Tag === 'button' ? { type: 'button' } : {})}
      className={`pk-ring-anim ${className}`}
      style={{
        position: 'relative', flexShrink: 0, width: size, height: size, padding: 0,
        border: `${border}px solid transparent`, borderRadius: '50%',
        background: ringBorder(fill), boxSizing: 'border-box', overflow: 'hidden',
        display: 'block', ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** A photo filling an IrisCircle, inset so the ring stays a ring. */
export function CircleImage({ src, inset = 4, objectPosition = '50% 50%', alt = '' }) {
  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      referrerPolicy="no-referrer"
      style={{ position: 'absolute', inset, width: `calc(100% - ${inset * 2}px)`, height: `calc(100% - ${inset * 2}px)`, borderRadius: '50%', objectFit: 'cover', objectPosition, display: 'block' }}
    />
  );
}
